import React from 'react';
import { Box, Chip, Button, IconButton, TextField, Typography } from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  FilePresent as FilePresentIcon,
  MicOutlined as MicIcon,
} from '@mui/icons-material';

interface Suggestion {
  label: string;
  query: string;
}

interface ChatInputProps {
  routeSuggestions: Suggestion[];
  attachedFiles: File[];
  inputValue: string;
  setInputValue: (value: string) => void;
  isLoading: boolean;
  handleSuggestionClick: (query: string) => void;
  handleSendMessage: () => void;
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  removeFile: (fileName: string) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  inputRef: React.RefObject<HTMLInputElement>;
}

export const ChatInput: React.FC<ChatInputProps> = React.memo(
  ({
    routeSuggestions,
    attachedFiles,
    inputValue,
    setInputValue,
    isLoading,
    handleSuggestionClick,
    handleSendMessage,
    handleFileSelect,
    removeFile,
    fileInputRef,
    inputRef,
  }) => {
    return (
      <Box>
        {/* ATTACHED FILES PREVIEW ROW */}
        {attachedFiles.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              padding: '4px 16px 8px 16px',
              flexWrap: 'wrap',
              backgroundColor: '#FFFFFF',
            }}
          >
            {attachedFiles.map((file, idx) => (
              <Chip
                key={idx}
                icon={<FilePresentIcon sx={{ fontSize: 16 }} />}
                label={`${file.name.substring(0, 15)}${file.name.length > 15 ? '...' : ''}`}
                size="small"
                onDelete={() => removeFile(file.name)}
                variant="outlined"
                sx={{
                  fontSize: '11px',
                  backgroundColor: '#F5F5F7',
                  borderColor: '#E0E0E0',
                  '& .MuiChip-deleteIcon': {
                    fontSize: '14px',
                    color: '#757575',
                    '&:hover': { color: '#111111' },
                  },
                }}
              />
            ))}
          </Box>
        )}

        {/* QUICK SUGGESTIONS ROW */}
        {routeSuggestions.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              padding: '4px 16px 10px 16px',
              flexWrap: 'nowrap',
              overflowX: 'auto',
              backgroundColor: '#FFFFFF',
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            {routeSuggestions.map((sug, idx) => (
              <Button
                key={idx}
                variant="outlined"
                onClick={() => handleSuggestionClick(sug.query)}
                aria-label={`Suggestion query: ${sug.label}`}
                sx={{
                  borderRadius: '16px',
                  border: '1px solid #EAEAEA',
                  backgroundColor: '#FFFFFF',
                  color: '#424242',
                  padding: '6px 14px',
                  fontSize: '12px',
                  fontWeight: 500,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  minWidth: 'auto',
                  '&:hover': {
                    backgroundColor: '#F5F5F5',
                    borderColor: '#D0D0D0',
                  },
                  '&:focus-visible': {
                    outline: '2px solid #111111',
                    outlineOffset: '2px',
                    borderColor: '#111111',
                  },
                }}
              >
                {sug.label}
              </Button>
            ))}
          </Box>
        )}

        {/* INPUT AREA */}
        <Box
          sx={{
            padding: '0px 16px 8px 16px',
            backgroundColor: '#FFFFFF',
            borderTop: '1px solid #F0F0F0',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: '1px solid #E0E0E0',
              borderRadius: '24px',
              padding: '4px 6px 4px 12px',
              backgroundColor: '#FFFFFF',
              boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.02)',
              mt: 1,
              transition: 'border-color 0.25s ease',
              '&:focus-within': {
                borderColor: '#111111',
              },
            }}
          >
            {/* Attachment Button */}
            <IconButton
              size="small"
              component="label"
              disabled={isLoading}
              aria-label="Attach file to chat"
              sx={{
                color: '#757575',
                mr: 0.5,
                '&:hover': { backgroundColor: '#F5F5F5' },
                '&:focus-visible': {
                  outline: '2px solid #111111',
                  outlineOffset: '2px',
                },
              }}
            >
              <AttachFileIcon sx={{ fontSize: 20 }} />
              <input
                ref={fileInputRef}
                type="file"
                hidden
                multiple
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.xlsx,.csv,.jpg,.png"
              />
            </IconButton>

            {/* Text Input */}
            <TextField
              inputRef={inputRef}
              fullWidth
              size="small"
              placeholder="Ask anything about your shipment..."
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyPress={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={isLoading}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                'aria-label': 'Ask anything about your shipment',
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '13.5px',
                  color: '#1A1A1A',
                  padding: '4px 0',
                },
              }}
            />

            {/* Microphone Button */}
            <IconButton
              size="small"
              disabled={isLoading}
              aria-label="Use voice input"
              sx={{
                color: '#757575',
                mx: 0.5,
                '&:hover': { backgroundColor: '#F5F5F5' },
                '&:focus-visible': {
                  outline: '2px solid #111111',
                  outlineOffset: '2px',
                },
              }}
            >
              <MicIcon sx={{ fontSize: 20 }} />
            </IconButton>

            {/* Send Button */}
            <IconButton
              onClick={() => handleSendMessage()}
              disabled={isLoading || (!inputValue.trim() && attachedFiles.length === 0)}
              aria-label="Send message"
              sx={{
                color: 'white',
                backgroundColor: '#111111',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: '#222222',
                },
                '&:disabled': {
                  backgroundColor: '#E0E0E0',
                  color: '#9E9E9E',
                },
                '&:focus-visible': {
                  outline: '2px solid #111111',
                  outlineOffset: '2px',
                },
              }}
            >
              <SendIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          {/* Disclaimer Text */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              color: '#9E9E9E',
              fontSize: '11px',
              mt: 1,
              mb: 0.5,
            }}
          >
            ShipMate AI may produce inaccurate info. Verify critical details.
          </Typography>
        </Box>
      </Box>
    );
  },
);

ChatInput.displayName = 'ChatInput';
